import React from 'react';

/**
 * Simple bar chart component for success rate visualization
 */
const SimpleBarChart = ({ data, title, xKey, yKey, height = 200 }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500 dark:text-gray-400">
        No data available
      </div>
    );
  }

  const maxValue = Math.max(...data.map(item => item[yKey]));
  const minValue = Math.min(...data.map(item => item[yKey]));
  const range = maxValue - minValue || 1;

  return (
    <div className="w-full">
      {title && (
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 dark:text-gray-600">
          {title}
        </h4>
      )}
      <div className="flex items-end space-x-2" style={{ height: `${height}px` }}>
        {data.map((item, index) => {
          const barHeight = ((item[yKey] - minValue) / range) * (height - 40);
          const percentage = typeof item[yKey] === 'number' ? item[yKey].toFixed(1) : item[yKey];
          
          return (
            <div key={index} className="flex-1 flex flex-col items-center">
              <div className="text-xs text-gray-600 dark:text-gray-400 mb-1 dark:text-gray-300 dark:text-gray-600">
                {percentage}%
              </div>
              <div
                className="w-full bg-teal-500 rounded-t transition-all duration-300 hover:bg-teal-600"
                style={{ height: `${Math.max(barHeight, 4)}px` }}
                title={`${item[xKey]}: ${percentage}%`}
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center truncate w-full">
                {item[xKey]}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Simple line chart component for trends
 */
const SimpleLineChart = ({ data, title, xKey, yKey, height = 200 }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500 dark:text-gray-400">
        No data available
      </div>
    );
  }

  const maxValue = Math.max(...data.map(item => item[yKey]));
  const minValue = Math.min(...data.map(item => item[yKey]));
  const range = maxValue - minValue || 1;
  const chartHeight = height - 60;

  // Generate SVG path for the line
  const pathData = data.map((item, index) => {
    const x = (index / (data.length - 1)) * 100;
    const y = 100 - ((item[yKey] - minValue) / range) * 100;
    return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  return (
    <div className="w-full">
      {title && (
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 dark:text-gray-600">
          {title}
        </h4>
      )}
      <div className="relative" style={{ height: `${height}px` }}>
        <svg
          className="w-full h-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <path
            d={pathData}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-teal-500"
          />
          {/* Data points */}
          {data.map((item, index) => {
            const x = (index / (data.length - 1)) * 100;
            const y = 100 - ((item[yKey] - minValue) / range) * 100;
            return (
              <circle
                key={index}
                cx={x}
                cy={y}
                r="2"
                fill="currentColor"
                className="text-teal-600"
              />
            );
          })}
        </svg>
        
        {/* X-axis labels */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-gray-500 dark:text-gray-400">
          {data.map((item, index) => (
            <span key={index} className="truncate">
              {new Date(item[xKey]).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Simple donut chart for failure reasons
 */
const SimpleDonutChart = ({ data, title, labelKey, valueKey, height = 200 }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500 dark:text-gray-400">
        No data available
      </div>
    );
  }

  const total = data.reduce((sum, item) => sum + item[valueKey], 0);
  const colors = [
    'text-red-500',
    'text-orange-500', 
    'text-yellow-500',
    'text-blue-500',
    'text-purple-500',
    'text-pink-500'
  ];

  let cumulativePercentage = 0;

  return (
    <div className="w-full">
      {title && (
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 dark:text-gray-600">
          {title}
        </h4>
      )}
      <div className="flex items-center space-x-4">
        <div className="relative" style={{ width: height, height }}>
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="currentColor"
              strokeWidth="8"
              className="text-gray-200 dark:text-gray-700"
            />
            {data.map((item, index) => {
              const percentage = (item[valueKey] / total) * 100;
              const strokeDasharray = `${percentage * 2.51} 251`;
              const strokeDashoffset = -cumulativePercentage * 2.51;
              cumulativePercentage += percentage;
              
              return (
                <circle
                  key={index}
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="8"
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                  className={colors[index % colors.length]}
                />
              );
            })}
          </svg>
        </div>
        
        <div className="flex-1 space-y-2">
          {data.map((item, index) => {
            const percentage = ((item[valueKey] / total) * 100).toFixed(1);
            return (
              <div key={index} className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${colors[index % colors.length].replace('text-', 'bg-')}`} />
                <span className="text-sm text-gray-700 dark:text-gray-300 dark:text-gray-600">
                  {item[labelKey]} ({percentage}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export { SimpleBarChart, SimpleLineChart, SimpleDonutChart };
